/**
 * Suggest-details documentation for schema-backed completion candidates.
 *
 * Monaco's suggest widget renders `detail` inline (right-aligned, subtle) and
 * `documentation` in the expandable side panel. The resolvers only ever set
 * the short `detail` string, so the panel used to open empty. This module is
 * the single shared post-pass (called by all 5 per-engine resolvers right
 * before ranking) that fills `documentation` with markdown for:
 *   - column candidates — object identity, data type, nullability, PK, and
 *     the FK target when a snapshot FK edge covers the column;
 *   - table/view candidates — a capped one-per-line column/type preview.
 *
 * Resolution is deliberately conservative: a column candidate is documented
 * only when its `detail` qualifier (`TYPE (alias|table|schema.table)`)
 * resolves through the SAME `resolveColumnRefToTable` seam the semantic
 * hover uses AND the detail's type prefix matches the resolved column — a
 * mismatch means the qualifier collided with an unrelated relation, and no
 * docs beat wrong docs. CTE/subquery/system-table emissions have no snapshot
 * metadata and simply stay documentation-free.
 */

import type { AliasBinding, CompletionCandidate, FkEdge } from '@/core/engines';

import { resolveColumnRefToTable } from '../context/resolveColumnRefToTable';

// Structural subset of the bus ColumnMeta/TableMeta — the per-engine
// resolvers narrow their local SnapshotTable types, so everything beyond
// name/dataType is optional here (missing fields just omit their line).
export type DocsColumn = {
  readonly name: string;
  readonly dataType: string;
  readonly nullable?: boolean;
  readonly isPrimaryKey?: boolean;
  readonly comment?: string;
};

export type DocsTable = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly kind?: string;
  readonly columns: ReadonlyArray<DocsColumn>;
  readonly comment?: string;
};

export type CompletionDocsArgs = {
  readonly aliasBindings: readonly AliasBinding[];
  readonly snapshotTables: ReadonlyArray<DocsTable>;
  readonly foreignKeys: readonly FkEdge[];
  /** The engine's identifier fold (unquote + case fold) — same one the
   *  resolver passes to `resolveColumnRefToTable` elsewhere. */
  readonly fold: (id: string) => string;
};

const MAX_TABLE_PREVIEW_COLUMNS = 12;

export function enrichCandidatesWithDocs(
  candidates: readonly CompletionCandidate[],
  args: CompletionDocsArgs,
): readonly CompletionCandidate[] {
  if (args.snapshotTables.length === 0) return candidates;
  // Lazy per-call caches: the table index makes thousands of table candidates
  // O(1) each; the column memo dedupes repeated (detail, label) pairs.
  let tableIndex: Map<string, DocsTable[]> | null = null;
  const columnMemo = new Map<string, string | undefined>();
  return candidates.map((c) => {
    if (c.documentation) return c;
    if (c.kind === 'column') {
      const key = `${c.detail ?? ''}\u0000${c.label}`;
      if (!columnMemo.has(key)) columnMemo.set(key, columnDocsFor(c, args));
      const docs = columnMemo.get(key);
      return docs ? { ...c, documentation: docs } : c;
    }
    if (c.kind === 'table' || c.kind === 'view') {
      tableIndex ??= buildTableIndex(args.snapshotTables, args.fold);
      const docs = tableDocsFor(c, tableIndex, args.fold);
      return docs ? { ...c, documentation: docs } : c;
    }
    return c;
  });
}

// ─── column candidates ───────────────────────────────────────────────────────

function columnDocsFor(
  c: CompletionCandidate,
  args: CompletionDocsArgs,
): string | undefined {
  const parsed = parseColumnDetail(c.detail);
  if (!parsed) return undefined;
  const resolved = resolveColumnRefToTable(
    `${parsed.qualifier}.${c.label}`,
    args.aliasBindings,
    args.snapshotTables,
    args.fold,
  );
  if (!resolved) return undefined;
  const column = resolved.column as DocsColumn;
  // Type-prefix guard — the emitting site wrote the SAME dataType into the
  // detail, so a mismatch means the qualifier resolved to the wrong relation.
  if (parsed.dataType.toLowerCase() !== column.dataType.toLowerCase()) return undefined;
  return buildColumnDocs(resolved.table, column, args.foreignKeys, args.fold);
}

type ParsedColumnDetail = { readonly dataType: string; readonly qualifier: string };

/** Parse the resolver detail convention `TYPE (qualifier)` where qualifier
 *  may be `alias`, `table`, `schema.table`, `PK, table`, `NEW → table`, or
 *  `table via %ROWTYPE`. Returns null for anything else (no type prefix, no
 *  parenthesized context, multi-word contexts like `parameter of proc`). */
function parseColumnDetail(detail: string | undefined): ParsedColumnDetail | null {
  if (!detail) return null;
  const m = /^(.+?)\s*\(([^()]+)\)$/.exec(detail.trim());
  if (!m) return null;
  let qual = m[2]!.trim();
  const arrow = qual.lastIndexOf('→');
  if (arrow >= 0) qual = qual.slice(arrow + 1).trim();
  const comma = qual.lastIndexOf(',');
  if (comma >= 0) qual = qual.slice(comma + 1).trim();
  qual = qual.split(/\s+/, 1)[0] ?? '';
  const segments = qual.split('.').filter((s) => s.length > 0);
  const last = segments[segments.length - 1];
  if (!last) return null;
  return { dataType: m[1]!.trim(), qualifier: last };
}

function buildColumnDocs(
  table: DocsTable,
  column: DocsColumn,
  foreignKeys: readonly FkEdge[],
  fold: (id: string) => string,
): string {
  const facts: string[] = [`\`${column.dataType}\``];
  if (typeof column.nullable === 'boolean') {
    facts.push(column.nullable ? 'nullable' : 'NOT NULL');
  }
  if (column.isPrimaryKey === true) facts.push('PK');
  const fkTarget = fkTargetFor(table, column, foreignKeys, fold);
  if (fkTarget) facts.push(`FK → \`${fkTarget}\``);
  const lines = [`\`${qualifiedName(table)}.${column.name}\``, facts.join(' · ')];
  if (column.comment) lines.push(column.comment);
  return lines.join('\n\n');
}

/** First FK edge that leaves `table.column` → `schema.table (column)`. */
function fkTargetFor(
  table: DocsTable,
  column: DocsColumn,
  foreignKeys: readonly FkEdge[],
  fold: (id: string) => string,
): string | undefined {
  for (const e of foreignKeys) {
    if (fold(e.fromTable) !== fold(table.name)) continue;
    if (e.fromSchema && table.schema && fold(e.fromSchema) !== fold(table.schema)) continue;
    const idx = e.fromColumns.findIndex((n) => fold(n) === fold(column.name));
    if (idx < 0) continue;
    const toColumn = e.toColumns[idx] ?? e.toColumns[0];
    const target = `${e.toSchema ? `${e.toSchema}.` : ''}${e.toTable}`;
    return toColumn ? `${target} (${toColumn})` : target;
  }
  return undefined;
}

// ─── table / view candidates ─────────────────────────────────────────────────

function buildTableIndex(
  tables: ReadonlyArray<DocsTable>,
  fold: (id: string) => string,
): Map<string, DocsTable[]> {
  const index = new Map<string, DocsTable[]>();
  for (const t of tables) {
    const key = fold(t.name);
    const bucket = index.get(key);
    if (bucket) bucket.push(t);
    else index.set(key, [t]);
  }
  return index;
}

function tableDocsFor(
  c: CompletionCandidate,
  index: Map<string, DocsTable[]>,
  fold: (id: string) => string,
): string | undefined {
  // Labels are `name` or `schema.name` (possibly quoted — fold unquotes).
  const parts = c.label.split('.').map((p) => p.trim()).filter((p) => p.length > 0);
  const name = parts[parts.length - 1];
  if (!name) return undefined;
  const qualifier = parts.length > 1 ? fold(parts[parts.length - 2]!) : undefined;
  const matches = index.get(fold(name)) ?? [];
  // Unqualified + several same-name tables: first match (accepted ambiguity,
  // same rule the semantic hover applies to bare column refs).
  const table = qualifier
    ? matches.find(
        (t) => fold(t.schema ?? '') === qualifier || fold(t.database ?? '') === qualifier,
      )
    : matches[0];
  if (!table || table.columns.length === 0) return undefined;
  return buildTableDocs(table);
}

function buildTableDocs(table: DocsTable): string {
  const kindLabel = (table.kind ?? 'table').includes('view') ? 'view' : 'table';
  const total = table.columns.length;
  const preview = table.columns.slice(0, MAX_TABLE_PREVIEW_COLUMNS);
  const width = Math.max(...preview.map((c) => c.name.length));
  const rows = preview.map((c) => `${c.name.padEnd(width)}  ${c.dataType}`.trimEnd());
  const lines = [
    `\`${qualifiedName(table)}\` — ${kindLabel} · ${total} column${total === 1 ? '' : 's'}`,
    ['```', ...rows, '```'].join('\n'),
  ];
  const more = total - preview.length;
  if (more > 0) lines.push(`… +${more} more`);
  if (table.comment) lines.push(table.comment);
  return lines.join('\n\n');
}

function qualifiedName(table: DocsTable): string {
  const qualifier = table.schema ?? table.database;
  return `${qualifier ? `${qualifier}.` : ''}${table.name}`;
}
